import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { BookOpen, ArrowRight, Clock, Sparkles } from "lucide-react";

/**
 * Recommended-reading panel for the dashboard.
 *
 * Pulls /api/learn/recommended which intersects the signed-in user's
 * support_tags with each article's tags. Falls back to the latest
 * published articles when the user has no matching tags yet — so the
 * dashboard never feels empty.
 *
 * Tone: wellness-first, calm, supportive — never "fix your condition".
 */
export default function RecommendedReading({ limit = 3 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get(`/learn/recommended?limit=${limit}`)
      .then((r) => mounted && setData(r.data))
      .catch(() => mounted && setError(true));
    return () => {
      mounted = false;
    };
  }, [limit]);

  if (error || !data || (data.items || []).length === 0) return null;

  const isMatched = !!data.matched;

  return (
    <Card className="rounded-2xl border-emerald-100 overflow-hidden" data-testid="dashboard-recommended-reading">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              {isMatched ? "Picked for your wellness journey" : "Supportive wellness reading"}
            </div>
            <h3 className="font-display text-xl sm:text-2xl text-emerald-950 mt-1.5 leading-snug">
              {isMatched
                ? "Recommended reading for your wellness journey"
                : "A few gentle reads to start with"}
            </h3>
            {isMatched ? (
              <p className="text-sm text-emerald-900/65 mt-1 max-w-xl leading-relaxed">
                Based on what you've shared, these calm, evidence-led pieces tend to resonate most.
              </p>
            ) : null}
          </div>
          <Link
            to="/learn"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-900 group shrink-0"
            data-testid="dashboard-recommended-see-all"
          >
            All articles
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <ul className="mt-5 space-y-3" data-testid="dashboard-recommended-list">
          {(data.items || []).slice(0, limit).map((art) => (
            <li key={art.id || art.slug}>
              <RecommendedCard article={art} />
            </li>
          ))}
        </ul>

        <Link
          to="/learn"
          className="sm:hidden mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-900 group"
        >
          Browse all articles
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

function RecommendedCard({ article }) {
  const slug = article.slug;
  const minutes = article.estimated_read_minutes;
  const matched = article.matched_tags || [];
  return (
    <Link
      to={`/learn/${slug}`}
      data-testid={`dashboard-recommended-${slug}`}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 rounded-xl"
    >
      <div className="flex items-start gap-3 p-4 rounded-xl border border-emerald-100 bg-white hover:border-emerald-200 hover:shadow-sm transition-all group">
        <div className="shrink-0 mt-0.5 w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
          <BookOpen className="w-4 h-4 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-emerald-950 leading-snug">{article.title}</div>
          {article.hero_excerpt ? (
            <p className="text-xs text-emerald-900/65 mt-1 leading-relaxed line-clamp-2">
              {article.hero_excerpt}
            </p>
          ) : null}
          <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px] text-emerald-900/60">
            {minutes ? (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {minutes} min read
              </span>
            ) : null}
            {matched.slice(0, 2).map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100 px-2 py-0.5 capitalize"
              >
                {t.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-emerald-700 shrink-0 mt-2 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}
